import type { CSSProperties } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton, InputAdornment, Link, Stack,
  TextField, Typography,
} from '@mui/material';
import { FlightTakeoffRounded, Visibility, VisibilityOff } from '@mui/icons-material';
import { Controller } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { APP_NAME } from '@/config/productBrand';
import LoginHero from './LoginHero';
import LoginCard from './LoginCard';
import SkylineLogin from './skyline/SkylineLogin';
import { useLoginForm, type LoginTemplateProps } from './useLoginForm';
import LoginOtpPanel from './LoginOtpPanel';
import '@/pages/auth/tripzaLogin.css';

/** The designs a company can choose for its own domain (BrandingSettings.LoginTemplate on the API). */
export type LoginTemplateId = 'Skyline' | 'Journey' | 'Classic';

export const LOGIN_TEMPLATES: { id: LoginTemplateId; name: string; description: string; swatch: string }[] = [
  {
    id: 'Skyline', name: 'Skyline',
    description: 'Full-screen travel photograph with a glass sign-in card. The TravelERP default.',
    swatch: 'linear-gradient(135deg,#0B1028 0%,#2E3A80 55%,#A86EC4 100%)',
  },
  {
    id: 'Journey', name: 'Journey',
    description: 'Dark world map with destination pins, in your brand colour.',
    swatch: 'linear-gradient(135deg,#1a0507 0%,#3a0b10 60%,#E0202F 100%)',
  },
  {
    id: 'Classic', name: 'Classic',
    description: 'Clean and simple: your colour and logo on the left, a white form on the right.',
    swatch: 'linear-gradient(90deg,#1D4E89 0 45%,#F4F6FA 45% 100%)',
  },
];

/** One of the designs, by id; an unknown id falls back to Skyline. */
export function LoginTemplateView({ template, ...props }: LoginTemplateProps & { template: LoginTemplateId | null | undefined }) {
  if (template === 'Journey') return <JourneyLogin {...props} />;
  if (template === 'Classic') return <ClassicLogin {...props} />;
  return <SkylineLogin {...props} />;
}

/** Journey — the map scene on the left, the card on the right, the card alone below 980px. */
export function JourneyLogin({ brand, initialEmail, preview }: LoginTemplateProps) {
  // The design's accent is its --red; a company's own colour takes its place.
  const style = (brand ? { '--red': brand.primaryColor, '--red2': brand.primaryColor } : undefined) as CSSProperties | undefined;
  return (
    <main className="tz" style={style}>
      <LoginHero brand={brand} />
      <LoginCard brand={brand} initialEmail={initialEmail} preview={preview} />
    </main>
  );
}

/** Classic — the company's colour, logo and name on the left; a plain white form on the right. */
export function ClassicLogin({ brand, initialEmail, preview }: LoginTemplateProps) {
  const { form, onSubmit, notice, error, busy, showPwd, toggleShowPwd, remember, setRemember, otp } =
    useLoginForm(initialEmail, preview);
  const colour = brand?.primaryColor ?? '#1D4E89';
  const name = brand?.companyName ?? APP_NAME;

  return (
    <Box component="main" sx={{ minHeight: '100vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, bgcolor: '#F4F6FA' }}>
      {/* Left — brand panel */}
      <Box
        sx={{
          width: { xs: '100%', md: '45%' }, flexShrink: 0, color: '#fff',
          background: `linear-gradient(160deg, ${colour} 0%, color-mix(in srgb, ${colour} 70%, #000) 100%)`,
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          px: { xs: 3, sm: 6 }, py: { xs: 4, md: 7 }, gap: 4,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {brand?.logoUrl ? (
            <Box component="img" src={brand.logoUrl} alt={name}
              sx={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain', bgcolor: '#fff', borderRadius: 1.5, p: 0.75 }} />
          ) : (
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.16)', display: 'grid', placeItems: 'center' }}>
              <FlightTakeoffRounded />
            </Box>
          )}
          <Typography sx={{ fontWeight: 700, fontSize: 20, wordBreak: 'break-word' }}>{name}</Typography>
        </Stack>

        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Typography sx={{ fontWeight: 800, fontSize: { md: 34, lg: 40 }, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
            Your travel business, in one place.
          </Typography>
          <Typography sx={{ mt: 2, fontSize: 16, opacity: 0.85, maxWidth: 420, lineHeight: 1.6 }}>
            Enquiries, quotes, bookings and travellers — sign in to pick up where you left off.
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ opacity: 0.7, display: { xs: 'none', md: 'block' } }}>
          {brand ? `Powered by ${APP_NAME}` : `© ${new Date().getFullYear()} ${APP_NAME}`}
        </Typography>
      </Box>

      {/* Right — the form */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2.5, sm: 5 } }}>
        <Box sx={{ width: '100%', maxWidth: 420, bgcolor: '#fff', borderRadius: 3, p: { xs: 3, sm: 4.5 }, boxShadow: '0 12px 40px rgba(15,23,42,0.08)', border: '1px solid #E5E9F0' }}>
          <Typography sx={{ fontWeight: 800, fontSize: 26, color: '#0F172A' }}>Sign in</Typography>
          <Typography sx={{ color: '#64748B', fontSize: 14, mt: 0.5, mb: 3 }}>
            {brand ? `Welcome back to ${brand.companyName}.` : 'Welcome back. Sign in to your account.'}
          </Typography>

          {otp.step ? <LoginOtpPanel otp={otp} accent={colour} /> : (
          <Box component="form" onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              {notice && !error && <Alert severity="info">{notice}</Alert>}
              {error && <Alert severity="error">{error}</Alert>}

              <Controller
                name="email" control={form.control}
                render={({ field, fieldState }) => (
                  <TextField {...field} label="Email" type="email" fullWidth autoComplete="username" autoFocus={!preview}
                    error={!!fieldState.error} helperText={fieldState.error?.message} />
                )}
              />
              <Controller
                name="password" control={form.control}
                render={({ field, fieldState }) => (
                  <TextField {...field} label="Password" type={showPwd ? 'text' : 'password'} fullWidth autoComplete="current-password"
                    error={!!fieldState.error} helperText={fieldState.error?.message}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={toggleShowPwd} edge="end" size="small" aria-label={showPwd ? 'Hide password' : 'Show password'}>
                              {showPwd ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <FormControlLabel
                  control={<Checkbox size="small" checked={remember} onChange={(e) => setRemember(e.target.checked)} sx={{ '&.Mui-checked': { color: colour } }} />}
                  label={<Typography variant="body2">Remember me</Typography>}
                />
                <Link component={RouterLink} to="/forgot-password" variant="body2" fontWeight={600} underline="hover" sx={{ color: colour }}>
                  Forgot password?
                </Link>
              </Stack>

              <Button
                type="submit" fullWidth size="large" variant="contained" disabled={busy || preview}
                sx={{ height: 48, fontWeight: 700, textTransform: 'none', bgcolor: colour, '&:hover': { bgcolor: colour, filter: 'brightness(1.08)' } }}
              >
                {busy ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}
              </Button>
            </Stack>
          </Box>
          )}

          {!brand && (
            <Typography align="center" variant="body2" sx={{ mt: 3, color: '#64748B' }}>
              New to {APP_NAME}?{' '}
              <Link component={RouterLink} to="/onboarding" fontWeight={700} underline="hover">Create Workspace</Link>
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
